import {
  CmpOutboundEmailKindEnum,
  CmpOutboundEmailStatusEnum,
} from "../../../graphql/types";
import {
  STAFF_SUPPORT_EMAIL,
  UserFacingError,
} from "../../../utils/userFacingError";

export type OutboundEmailDelivery = {
  kind?: CmpOutboundEmailKindEnum | null;
  status?: CmpOutboundEmailStatusEnum | null;
  toEmail?: string | null;
  intendedToEmail?: string | null;
  recipientOverrideUsed?: boolean | null;
  errorMessage?: string | null;
  invoiceId?: string | null;
  createdAtUtcMilli?: number | null;
  attemptedAtUtcMilli?: number | null;
  sentAtUtcMilli?: number | null;
  failedAtUtcMilli?: number | null;
};

type SavedInvoiceRecipientSource = Readonly<{
  billingSnapshot?: Readonly<{ contactEmail?: string | null }> | null;
  billToSnapshot?: Readonly<{ contactEmail?: string | null }> | null;
}>;

type FindLatestOutboundEmailInput = {
  emails?: ReadonlyArray<OutboundEmailDelivery> | null;
  kind: CmpOutboundEmailKindEnum;
  invoiceId?: string | null;
  intendedRecipientEmail?: string | null;
};

const normalizeEmail = (value?: string | null) =>
  (value || "").trim().toLowerCase();

export const getValidInvoiceEmailRecipient = (
  value?: string | null
): string | null => {
  const normalizedEmail = (value || "").trim();

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
    ? normalizedEmail
    : null;
};

export const getSavedInvoiceRecipientEmail = ({
  billingSnapshot,
  billToSnapshot,
}: SavedInvoiceRecipientSource): string | null =>
  billToSnapshot?.contactEmail?.trim() ||
  billingSnapshot?.contactEmail?.trim() ||
  null;

const getOutboundEmailTimestamp = (email: OutboundEmailDelivery) =>
  email.sentAtUtcMilli ||
  email.failedAtUtcMilli ||
  email.attemptedAtUtcMilli ||
  email.createdAtUtcMilli ||
  0;

export const findLatestOutboundEmail = ({
  emails,
  kind,
  invoiceId,
  intendedRecipientEmail,
}: FindLatestOutboundEmailInput): OutboundEmailDelivery | null => {
  const normalizedIntendedRecipientEmail =
    normalizeEmail(intendedRecipientEmail);

  return (
    (emails || [])
      .filter((email) => {
        if (email.kind !== kind) {
          return false;
        }

        if (invoiceId && email.invoiceId !== invoiceId) {
          return false;
        }

        if (!normalizedIntendedRecipientEmail) {
          return true;
        }

        return [email.intendedToEmail, email.toEmail]
          .map(normalizeEmail)
          .includes(normalizedIntendedRecipientEmail);
      })
      .reduce<OutboundEmailDelivery | null>(
        (latest, email) =>
          !latest ||
          getOutboundEmailTimestamp(email) > getOutboundEmailTimestamp(latest)
            ? email
            : latest,
        null
      )
  );
};

export const wasOutboundEmailSent = (
  email?: OutboundEmailDelivery | null
): email is OutboundEmailDelivery =>
  email?.status === CmpOutboundEmailStatusEnum.Sent;

// UX audit INV-11: SENDING and DELIVERY_UNKNOWN may still deliver. A resend
// could email the member twice, so Send stays disabled for that result.
export const isOutboundEmailDeliveryPending = (
  email?: OutboundEmailDelivery | null
) =>
  email?.status === CmpOutboundEmailStatusEnum.Sending ||
  email?.status === CmpOutboundEmailStatusEnum.DeliveryUnknown;

const invoiceEmailDeliveryPendingErrorName = "InvoiceEmailDeliveryPendingError";

// The backend lease for a SENDING email is 5 minutes. DELIVERY_UNKNOWN never
// clears by itself and the backend allows a new send, so the block is time
// limited: a stuck record must not stop this invoice's email for good.
export const INVOICE_EMAIL_PENDING_BLOCK_UTC_MILLI = 10 * 60 * 1000;

export const RECENT_PENDING_INVOICE_EMAIL_MESSAGE =
  "The last email for this invoice is still sending, or we could not confirm that it was delivered. To stop a second copy, you can send it again 10 minutes after the last try. Close this window and open it again after that.";

/**
 * UX audit INV-11: the newest saved email of these kinds, when it is still
 * SENDING or DELIVERY_UNKNOWN and was attempted in the last 10 minutes.
 * A later SENT, FAILED or SKIPPED email clears it.
 */
export const findRecentPendingInvoiceEmail = ({
  emails,
  kinds,
  nowUtcMilli,
}: {
  emails?: ReadonlyArray<OutboundEmailDelivery> | null;
  kinds: ReadonlyArray<CmpOutboundEmailKindEnum>;
  nowUtcMilli: number;
}): OutboundEmailDelivery | null => {
  const latest = (emails || [])
    .filter((email) => Boolean(email.kind && kinds.includes(email.kind)))
    .reduce<OutboundEmailDelivery | null>(
      (current, email) =>
        !current ||
        getOutboundEmailTimestamp(email) > getOutboundEmailTimestamp(current)
          ? email
          : current,
      null
    );
  const attemptedAtUtcMilli = latest ? getOutboundEmailTimestamp(latest) : 0;

  return latest &&
    isOutboundEmailDeliveryPending(latest) &&
    attemptedAtUtcMilli > 0 &&
    nowUtcMilli - attemptedAtUtcMilli < INVOICE_EMAIL_PENDING_BLOCK_UTC_MILLI
    ? latest
    : null;
};

export const isInvoiceEmailDeliveryPendingError = (error: unknown) =>
  error instanceof Error && error.name === invoiceEmailDeliveryPendingErrorName;

export const wasOutboundEmailDeliveryConfirmed = (
  actionCompletedSuccessfully?: boolean | null,
  email?: OutboundEmailDelivery | null
): email is OutboundEmailDelivery =>
  actionCompletedSuccessfully === true && wasOutboundEmailSent(email);

const formatFailureRoutingDetails = (
  email?: OutboundEmailDelivery | null
): string[] => {
  if (!email) {
    return [];
  }

  const intendedRecipient = (email.intendedToEmail || "").trim();
  const recordedRecipient = (email.toEmail || "").trim();

  if (email.recipientOverrideUsed) {
    return [
      intendedRecipient ? `Intended recipient: ${intendedRecipient}.` : "",
      recordedRecipient
        ? `Configured test recipient: ${recordedRecipient}.`
        : "",
    ].filter(Boolean);
  }

  // Legacy SKIPPED records can carry the historical test-inbox fallback in
  // toEmail without recipientOverrideUsed being set. A skipped message was not
  // delivered, so only the intended recipient is trustworthy for user-facing
  // routing feedback.
  if (email.status === CmpOutboundEmailStatusEnum.Skipped) {
    return intendedRecipient
      ? [`Intended recipient: ${intendedRecipient}.`]
      : [];
  }

  if (
    email.status === CmpOutboundEmailStatusEnum.Failed ||
    email.status === CmpOutboundEmailStatusEnum.DeliveryUnknown
  ) {
    return [
      intendedRecipient ? `Intended recipient: ${intendedRecipient}.` : "",
      recordedRecipient ? `Attempted recipient: ${recordedRecipient}.` : "",
    ].filter(Boolean);
  }

  if (
    intendedRecipient &&
    recordedRecipient &&
    normalizeEmail(intendedRecipient) !== normalizeEmail(recordedRecipient)
  ) {
    return [
      `Intended recipient: ${intendedRecipient}.`,
      `Recorded recipient: ${recordedRecipient}.`,
    ];
  }

  const recipient = intendedRecipient || recordedRecipient;
  return recipient ? [`Recipient: ${recipient}.`] : [];
};

// A label can start in lower case ("the email for invoice 1045") so it also
// reads well in the middle of a sentence.
const toSentenceStart = (label: string) =>
  `${label.charAt(0).toUpperCase()}${label.slice(1)}`;

export const getOutboundEmailFailureMessage = ({
  email,
  label,
}: {
  email?: OutboundEmailDelivery | null;
  label: string;
}) => {
  const sentenceLabel = toSentenceStart(label);

  if (!email) {
    return `${sentenceLabel} was not sent. Try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`;
  }

  const statusMessage =
    email.status === CmpOutboundEmailStatusEnum.Failed
      ? `${sentenceLabel} was not delivered.`
      : email.status === CmpOutboundEmailStatusEnum.DeliveryUnknown
        ? `We could not confirm if ${label} was delivered. Do not send it again until you check with the recipient.`
        : email.status === CmpOutboundEmailStatusEnum.Sending
          ? `${sentenceLabel} is still sending. Do not send it again yet.`
      : email.status === CmpOutboundEmailStatusEnum.Skipped
        ? `${sentenceLabel} was not sent.`
        : email.status === CmpOutboundEmailStatusEnum.Sent
          ? `We could not confirm that ${label} was sent. Check the invoice before you send it again.`
          : `${sentenceLabel} was not sent.`;
  // The server writes the reason. The raw status code is not shown.
  const reason = (email.errorMessage || "").trim();

  return [
    statusMessage,
    ...formatFailureRoutingDetails(email),
    reason ? `Reason: ${/[.!?]$/.test(reason) ? reason : `${reason}.`}` : "",
    email.status === CmpOutboundEmailStatusEnum.Failed
      ? "Check that the email address is correct, then try again."
      : "",
  ]
    .filter(Boolean)
    .join(" ");
};

/**
 * The error for an email that was not confirmed as sent. A pending delivery
 * is marked so the Send controls can stay disabled.
 */
export const createInvoiceEmailFailureError = ({
  email,
  label,
}: {
  email?: OutboundEmailDelivery | null;
  label: string;
}) => {
  // Staff-ready text built from the delivery result, so it stays on screen.
  const error = new UserFacingError(
    getOutboundEmailFailureMessage({ email, label })
  );

  if (isOutboundEmailDeliveryPending(email)) {
    error.name = invoiceEmailDeliveryPendingErrorName;
  }

  return error;
};

export const getOutboundEmailSuccessMessage = ({
  email,
  label,
  verb = "sent",
}: {
  email: OutboundEmailDelivery;
  label: string;
  verb?: "sent" | "resent";
}) => {
  if (!wasOutboundEmailSent(email)) {
    return getOutboundEmailFailureMessage({ email, label });
  }

  const intendedRecipient = (email.intendedToEmail || "").trim();
  const actualRecipient = (email.toEmail || "").trim();

  if (email.recipientOverrideUsed) {
    return [
      `${toSentenceStart(label)} was ${verb} to the configured test inbox${actualRecipient ? ` ${actualRecipient}` : ""}.`,
      intendedRecipient ? `Intended recipient: ${intendedRecipient}.` : "",
    ]
      .filter(Boolean)
      .join(" ");
  }

  const recipient = actualRecipient || intendedRecipient;
  return `${toSentenceStart(label)} was ${verb}${recipient ? ` to ${recipient}` : ""}.`;
};

// A list refresh cannot change the provider result already confirmed by the server.
export const confirmInvoiceEmailAndRefresh = async ({
  actionCompletedSuccessfully,
  email,
  label,
  refresh,
}: {
  actionCompletedSuccessfully?: boolean | null;
  email?: OutboundEmailDelivery | null;
  label: string;
  refresh: () => Promise<unknown>;
}): Promise<string> => {
  if (
    !wasOutboundEmailDeliveryConfirmed(actionCompletedSuccessfully, email) ||
    !email?.toEmail?.trim()
  ) {
    throw createInvoiceEmailFailureError({ email, label });
  }

  const message = getOutboundEmailSuccessMessage({ email, label });
  try {
    await refresh();
    return message;
  } catch {
    return `${message} The invoice list could not be refreshed. Refresh the page to see the latest status. Do not resend this email.`;
  }
};

export type InvoiceEmailSuccessContent = Readonly<{
  highlights: string[];
  message: string;
  note?: string;
  // The confirmed actual recipient (the test inbox when overridden).
  recipientEmail: string;
}>;

/**
 * Claude Design ActionSuccessModal copy for a confirmed invoice or receipt
 * email: "Email sent successfully to {email} for {member}!". The actual
 * recipient stays in the message. When the test-recipient override was used,
 * the note keeps the intended recipient visible (recipient-routing rule).
 */
export const getInvoiceEmailSuccessContent = ({
  customerName,
  email,
  kind = "INVOICE",
  refreshFailed = false,
}: {
  customerName: string;
  email: OutboundEmailDelivery;
  kind?: "INVOICE" | "RECEIPT";
  refreshFailed?: boolean;
}): InvoiceEmailSuccessContent => {
  const actualRecipient = (email.toEmail || "").trim();
  const intendedRecipient = (email.intendedToEmail || "").trim();
  const member = customerName.trim() || "the customer";
  const lead =
    kind === "RECEIPT" ? "Receipt emailed successfully" : "Email sent successfully";
  const notes = [
    email.recipientOverrideUsed
      ? `Sent to the configured test inbox.${
          intendedRecipient ? ` Intended recipient: ${intendedRecipient}.` : ""
        }`
      : "",
    refreshFailed
      ? "The invoice list could not be refreshed. Refresh the page to see the latest status. Do not resend this email."
      : "",
  ].filter(Boolean);

  return {
    highlights: [actualRecipient, member].filter(Boolean),
    message: `${lead} to ${actualRecipient || intendedRecipient} for ${member}!`,
    recipientEmail: actualRecipient,
    ...(notes.length ? { note: notes.join(" ") } : {}),
  };
};

/**
 * Confirms the authoritative provider result, then refreshes. The refresh
 * cannot change a confirmed delivery, so a refresh failure is reported and not
 * thrown.
 */
export const confirmInvoiceEmailDeliveryAndRefresh = async ({
  actionCompletedSuccessfully,
  email,
  label,
  refresh,
}: {
  actionCompletedSuccessfully?: boolean | null;
  email?: OutboundEmailDelivery | null;
  label: string;
  refresh: () => Promise<unknown>;
}): Promise<{ email: OutboundEmailDelivery; refreshFailed: boolean }> => {
  if (
    !wasOutboundEmailDeliveryConfirmed(actionCompletedSuccessfully, email) ||
    !email?.toEmail?.trim()
  ) {
    throw createInvoiceEmailFailureError({ email, label });
  }

  try {
    await refresh();
    return { email, refreshFailed: false };
  } catch {
    return { email, refreshFailed: true };
  }
};
